import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useNavigationGuard } from '../../contexts/NavigationGuardContext';

/**
 * Reusable breadcrumbs bar.
 * items: array of { label: string, path?: string }
 * The last item is automatically treated as the current (non-clickable) page.
 * Clicks route through the unsaved-changes navigation guard (same as the
 * sidebar), so a dirty form — e.g. the payroll calculator — prompts before
 * the breadcrumb navigates away.
 */
export default function BreadcrumbsBar({ items = [] }) {
  const navigate = useNavigate();
  const { checkNavigation } = useNavigationGuard();

  const allItems = [{ label: 'Home', path: '/dashboard' }, ...items];

  const handleItemClick = (path) => {
    const canNavigate = checkNavigation(() => navigate(path), path);
    if (canNavigate) navigate(path);
  };

  return (
    <Box
      sx={{
        backgroundColor: 'grey.50',
        px: { xs: 2, sm: 3 },
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'grey.200',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      {allItems.map((item, index) => {
        const isLast = index === allItems.length - 1;
        return (
          <React.Fragment key={`${item.label}-${index}`}>
            {isLast || !item.path ? (
              <Typography
                sx={{
                  fontSize: '14px',
                  color: 'grey.900',
                  fontWeight: 500,
                }}
              >
                {item.label}
              </Typography>
            ) : (
              <Typography
                onClick={() => handleItemClick(item.path)}
                sx={{
                  fontSize: '14px',
                  color: 'grey.500',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {item.label}
              </Typography>
            )}
            {!isLast && (
              <Typography
                sx={{
                  fontSize: '14px',
                  color: 'grey.400',
                  userSelect: 'none',
                }}
              >
                &gt;
              </Typography>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}
